import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Square, Send, Trash2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMOJIS = ["🔥", "💪", "⭐", "👏", "⚽", "🎯"];

export default function ProofReaction({ proof, token, onSaved, talkingPoint }) {
  const [emoji, setEmoji] = useState(proof.reaction_emoji || "");
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const hasExistingVoice = !!proof.voice_path;

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "audio/webm" });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        stream.getTracks().forEach((t) => t.stop());
      };
      recRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied. Allow mic to record a voice note.");
    }
  };

  const stopRec = () => {
    if (recRef.current && recording) { recRef.current.stop(); setRecording(false); }
  };

  // Coach taps a verdict cue -> jump the panel into view and auto-start the voice note.
  const panelRef = useRef(null);
  useEffect(() => {
    if (!talkingPoint) return;
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (!recording && !blob) startRec();
    // eslint-disable-next-line
  }, [talkingPoint]);

  const discard = () => { setBlob(null); setPreviewUrl(""); };

  const send = async () => {
    if (!emoji && !blob) { toast.error("Pick an emoji or record a voice note."); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("emoji", emoji);
    if (blob) fd.append("voice", blob, "reaction.webm");
    try {
      await axios.post(`${API}/coach/proofs/${proof.id}/react`, fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      toast.success("Reaction sent to the player!");
      discard();
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reaction.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={panelRef} className="mt-3 pt-3 border-t border-white/10" data-testid={`reaction-panel-${proof.id}`}>
      {talkingPoint && (
        <div data-testid={`talking-point-${proof.id}`} className="mb-3 rounded-md border border-[#F5C518]/40 bg-[#F5C518]/10 p-2.5">
          <div className="text-[9px] font-mono uppercase tracking-widest text-[#F5C518] mb-1 flex items-center gap-1"><Mic size={11} /> Talking point — read this as you record</div>
          <p className="text-sm text-white/90">{talkingPoint}</p>
        </div>
      )}
      <div className="text-[10px] font-bold uppercase tracking-widest text-white/45 mb-2">React with praise</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {EMOJIS.map((e) => (
          <button key={e} type="button" data-testid={`react-emoji-${e}-${proof.id}`} onClick={() => setEmoji(emoji === e ? "" : e)}
            className={`w-9 h-9 rounded-md text-lg flex items-center justify-center border transition-colors ${emoji === e ? "bg-[#C41E24]/20 border-[#C41E24]" : "bg-[#141414] border-white/10 hover:border-white/30"}`}>
            {e}
          </button>
        ))}
      </div>

      {hasExistingVoice && !blob && (
        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Current voice note</div>
          <audio controls preload="none" className="w-full h-9" src={`${API}/proofs/voice/${proof.id}?auth=${token}`} />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {!recording && !blob && (
          <button type="button" data-testid={`react-record-${proof.id}`} onClick={startRec}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#141414] border border-white/15 hover:border-[#C41E24] px-3 py-2 rounded-sm">
            <Mic size={14} className="text-[#C41E24]" /> {hasExistingVoice ? "Re-record" : "Record voice note"}
          </button>
        )}
        {recording && (
          <button type="button" data-testid={`react-stop-${proof.id}`} onClick={stopRec}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#C41E24] px-3 py-2 rounded-sm animate-pulse">
            <Square size={12} /> Stop recording
          </button>
        )}
        {blob && (
          <>
            <audio controls src={previewUrl} className="h-9 flex-1 min-w-[160px]" />
            <button type="button" onClick={discard} className="text-white/40 hover:text-[#C41E24]"><Trash2 size={15} /></button>
          </>
        )}
        <button type="button" data-testid={`react-send-${proof.id}`} onClick={send} disabled={busy || (!emoji && !blob)}
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-green-600/80 hover:bg-green-600 disabled:opacity-40 px-3 py-2 rounded-sm ml-auto">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
        </button>
      </div>
    </div>
  );
}
